'use client'

import type { FC } from 'react'
import { useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  CalendarDays,
  Clock,
  Search,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import type { BlogPostMeta, PostSearchIndex } from '@/lib/blog'
import {
  BlogGridSkeleton,
  BlogPaginationSkeleton,
} from '@/components/blog/skeletons'
import { urlFor } from '@/lib/sanityClient'

/** Debounce hook - delays value updates so Fuse search doesn't fire on every keystroke */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(id)
  }, [value, delayMs])
  return debounced
}

type Props = {
  serverPosts: BlogPostMeta[]
  page: number
  pagination: {
    totalPages: number
    hasNextPage: boolean
    hasPreviousPage: boolean
  }
  activeCategory?: string | null
  activeTag?: string | null
  initialQuery?: string | null
  pending?: boolean
  onPageChange: (page: number) => void
}

type FuseLike<T> = { search: (pattern: string) => Array<{ item: T }> }

const ClientSearch: FC<Props> = ({
  serverPosts,
  page,
  pagination,
  activeCategory,
  activeTag,
  initialQuery = '',
  pending = false,
  onPageChange,
}) => {
  const [q, setQ] = useState(initialQuery ?? '')
  const debouncedQ = useDebouncedValue(q, 250) // 250ms debounce
  const [searchPage, setSearchPage] = useState(1)

  // Reset search page when query or filters change
  useEffect(() => {
    setSearchPage(1)
  }, [debouncedQ, activeCategory, activeTag])

  // Lazy search: load Fuse + lightweight index only when user starts typing
  const [fuseInstance, setFuseInstance] =
    useState<FuseLike<PostSearchIndex> | null>(null)
  const [isIndexLoading, setIsIndexLoading] = useState(false)

  // Helper function to get optimized image URL
  const getOptimizedImageUrl = (image: any, width?: number) => {
    if (!image) return undefined
    if (typeof image === 'string') return image
    try {
      const builder = urlFor(image).format('webp')
      if (width) builder.width(width)
      return builder.url()
    } catch {
      return typeof image === 'object' && image.url ? image.url : undefined
    }
  }

  // Helper function to get blur data URL
  const getBlurDataURL = (image: any) => {
    if (!image || typeof image === 'string') return undefined
    try {
      return urlFor(image).width(24).blur(50).format('webp').url()
    } catch {
      return undefined
    }
  }

  useEffect(() => {
    const wantsSearch = debouncedQ.trim().length > 0
    if (!wantsSearch || fuseInstance) return

    let cancelled = false
    setIsIndexLoading(true)
    ;(async () => {
      try {
        const [{ default: Fuse }, idx] = await Promise.all([
          import('fuse.js'),
          fetch('/api/search-index').then(
            (r) => r.json() as Promise<PostSearchIndex[]>,
          ),
        ])
        if (cancelled) return
        const f = new (Fuse as any)(idx, {
          keys: [
            'title',
            'excerpt',
            'author',
            'category',
            'categories',
            'tags',
          ],
          threshold: 0.38,
          ignoreLocation: true,
          minMatchCharLength: 1,
        }) as FuseLike<PostSearchIndex>
        setFuseInstance(f)
      } finally {
        if (!cancelled) setIsIndexLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [debouncedQ, fuseInstance])

  const isSearching = debouncedQ.trim().length > 0
  const isSearchLoading = isSearching && !fuseInstance

  const results: PostSearchIndex[] = useMemo(() => {
    if (!isSearching || !fuseInstance) return []
    const base = fuseInstance.search(debouncedQ).map((r) => r.item)
    return base.filter((item) => {
      const catOK = activeCategory
        ? [item.category, ...(item.categories ?? [])].some(
            (c) =>
              (c ?? '').toLowerCase() === (activeCategory ?? '').toLowerCase(),
          )
        : true
      const tagOK = activeTag
        ? (item.tags ?? []).some(
            (t) => t.toLowerCase() === (activeTag ?? '').toLowerCase(),
          )
        : true
      return catOK && tagOK
    })
  }, [debouncedQ, isSearching, fuseInstance, activeCategory, activeTag])

  const showPending = pending || isIndexLoading || isSearchLoading

  // ---------- unified pagination ----------
  const SEARCH_PER_PAGE = 6

  const searchTotalPages = Math.max(
    1,
    Math.ceil(results.length / SEARCH_PER_PAGE),
  )
  const searchHasPrev = searchPage > 1
  const searchHasNext = searchPage < searchTotalPages

  // Slice search results for current page
  const paginatedResults = useMemo(() => {
    const start = (searchPage - 1) * SEARCH_PER_PAGE
    return results.slice(start, start + SEARCH_PER_PAGE)
  }, [results, searchPage])

  // Unified page values - pick search or server pagination
  const currentPage = isSearching ? searchPage : page
  const totalPages = isSearching ? searchTotalPages : pagination.totalPages
  const hasPrev = isSearching ? searchHasPrev : pagination.hasPreviousPage
  const hasNext = isSearching ? searchHasNext : pagination.hasNextPage

  const handlePageNav = (n: number) => {
    if (isSearching) {
      setSearchPage(n)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      onPageChange(n)
    }
  }

  // Build visible page numbers with ellipsis for mobile
  const getVisiblePages = (): (number | 'ellipsis')[] => {
    if (totalPages <= 5)
      return Array.from({ length: totalPages }, (_, i) => i + 1)

    const pages: (number | 'ellipsis')[] = []

    if (currentPage <= 3) {
      for (let i = 1; i <= Math.min(4, totalPages); i++) pages.push(i)
      if (totalPages > 4) pages.push('ellipsis', totalPages)
    } else if (currentPage >= totalPages - 2) {
      pages.push(1, 'ellipsis')
      for (let i = Math.max(totalPages - 3, 2); i <= totalPages; i++)
        pages.push(i)
    } else {
      pages.push(
        1,
        'ellipsis',
        currentPage - 1,
        currentPage,
        currentPage + 1,
        'ellipsis',
        totalPages,
      )
    }

    return pages
  }

  function baseCard(
    key: string,
    data: {
      slug: string
      image?: string | any
      category?: string
      title: string
      excerpt?: string
      date?: string
      readTime?: string | null
      isLcp?: boolean
      blurDataURL?: string
      authorName?: string
      authorImage?: string | any
    },
  ) {
    const initial = (data.authorName?.trim()?.[0] || 'A').toUpperCase()

    // Get optimized URLs
    const optimizedHeroImage = getOptimizedImageUrl(data.image)
    const optimizedAuthorImage = getOptimizedImageUrl(data.authorImage, 32)
    const blurDataURL = data.isLcp
      ? data.blurDataURL || getBlurDataURL(data.image)
      : undefined

    return (
      <article
        key={key}
        itemScope
        itemType="https://schema.org/BlogPosting"
        className="flex flex-col basis-full rounded-xl bg-white shadow-md ring-1 ring-border
                   overflow-hidden transition-transform will-change-transform hover:-translate-y-0.5
                   sm:basis-[calc(50%-0.75rem)] lg:basis-[calc(33.333%-1.333rem)]"
        style={
          data.isLcp
            ? undefined
            : {
                contentVisibility: 'auto',
                containIntrinsicSize: '384px 600px',
              }
        }
      >
        <Link
          href={`/blog/${data.slug}`}
          className="flex h-full flex-col"
          itemProp="url"
          prefetch={false}
        >
          {/* HERO IMAGE */}
          {optimizedHeroImage ? (
            <div className="relative h-40 sm:h-48 w-full">
              <Image
                src={optimizedHeroImage}
                alt={data.title}
                fill
                className="object-cover"
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                itemProp="image"
                priority={data.isLcp === true}
                fetchPriority={data.isLcp ? 'high' : undefined}
                loading={data.isLcp ? 'eager' : undefined}
                placeholder={data.isLcp && blurDataURL ? 'blur' : 'empty'}
                blurDataURL={blurDataURL}
              />
            </div>
          ) : (
            <div className="h-40 sm:h-48 w-full bg-emerald-50" />
          )}

          <div className="flex flex-1 flex-col p-4 sm:p-5 lg:p-6">
            {data.category && (
              <span itemProp="articleSection" className="sr-only">
                {data.category}
              </span>
            )}

            <h2
              className="mb-1.5 sm:mb-2 break-words text-base sm:text-lg lg:text-xl
                         font-semibold text-gray-900 hover:text-emerald-600 line-clamp-2"
              itemProp="headline"
            >
              {data.title}
            </h2>

            {data.excerpt && (
              <p
                className="mb-3 sm:mb-4 break-words text-sm sm:text-base text-gray-600
                           line-clamp-2 sm:line-clamp-3"
                itemProp="description"
              >
                {data.excerpt}
              </p>
            )}

            {data.date && (
              <meta
                itemProp="datePublished"
                content={new Date(data.date).toISOString()}
              />
            )}

            {/* Footer - stacks on mobile, side-by-side on sm+ */}
            <div
              className="mt-auto flex flex-col gap-2 sm:flex-row sm:items-center
                         sm:justify-between sm:gap-3 text-xs sm:text-sm text-gray-500"
            >
              {/* Author */}
              <div className="flex min-w-0 items-center gap-2">
                {optimizedAuthorImage ? (
                  <div className="relative h-7 w-7 sm:h-8 sm:w-8 shrink-0">
                    <Image
                      src={optimizedAuthorImage}
                      alt={
                        data.authorName
                          ? `${data.authorName} avatar`
                          : 'Author avatar'
                      }
                      fill
                      className="rounded-full object-cover ring-1 ring-border"
                      sizes="32px"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div
                    className="h-7 w-7 sm:h-8 sm:w-8 shrink-0 rounded-full bg-gray-200 ring-1 ring-border
                               flex items-center justify-center text-[10px] sm:text-xs font-medium text-gray-700"
                  >
                    {initial}
                  </div>
                )}
                {data.authorName && (
                  <span
                    className="truncate"
                    itemProp="author"
                    itemScope
                    itemType="https://schema.org/Person"
                  >
                    <span itemProp="name">{data.authorName}</span>
                  </span>
                )}
              </div>

              {/* Date + read time */}
              <div className="flex flex-shrink-0 items-center gap-3 sm:gap-4">
                {data.date && (
                  <div className="flex items-center gap-1">
                    <CalendarDays className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    <span>
                      {new Date(data.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                )}
                {data.readTime && (
                  <div className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    <span>{data.readTime}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Link>
      </article>
    )
  }

  function getAuthorName(p: any): string | undefined {
    return p.authorName ?? p.author?.name ?? p.author ?? undefined
  }

  function getAuthorImage(p: any): string | any | undefined {
    return p.authorImage ?? p.author?.image ?? undefined
  }

  function ixToCard(p: PostSearchIndex, isLcp = false) {
    return baseCard(p.slug, {
      slug: p.slug,
      image: p.image,
      category: p.category,
      title: p.title,
      excerpt: p.excerpt,
      date: p.date,
      readTime: p.readTime ?? null,
      isLcp,
      blurDataURL: (p as any).blurDataURL,
      authorName: getAuthorName(p),
      authorImage: getAuthorImage(p),
    })
  }

  function metaToCard(p: BlogPostMeta, isLcp = false) {
    return baseCard(p.slug, {
      slug: p.slug,
      image: p.image,
      category: p.category,
      title: p.title,
      excerpt: (p.excerpt ?? '').trim(),
      date: p.date,
      readTime: p.readTime ?? null,
      isLcp,
      blurDataURL: (p as any).blurDataURL,
      authorName: getAuthorName(p as any),
      authorImage: getAuthorImage(p as any),
    })
  }

  // Cards (mark first visible as LCP)
  const cards = isSearching
    ? paginatedResults.map((item: PostSearchIndex, i: number) =>
        ixToCard(item, i === 0),
      )
    : serverPosts.map((item: BlogPostMeta, i: number) =>
        metaToCard(item, i === 0),
      )

  const visiblePages = getVisiblePages()

  return (
    <div className="flex flex-col gap-6 sm:gap-8 md:gap-10">
      {/* Search Bar */}
      <div className="mx-auto w-full max-w-2xl mt-2 sm:mt-4 px-1 sm:px-0">
        <div className="relative flex">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 sm:h-5 sm:w-5 -translate-y-1/2 text-gray-400" />
          <Input
            type="search"
            placeholder="Search articles..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full py-2 pl-9 sm:pl-10 pr-4 text-sm sm:text-base"
            aria-label="Search articles"
          />
        </div>
        {/* Search results count */}
        {isSearching && !showPending && (
          <p className="mt-2 text-center text-xs sm:text-sm text-gray-500">
            {results.length} {results.length === 1 ? 'result' : 'results'} found
          </p>
        )}
      </div>

      {/* Grid or Skeleton */}
      {showPending ? (
        <BlogGridSkeleton />
      ) : (
        <div className="mx-auto w-full max-w-6xl flex flex-wrap items-stretch gap-4 sm:gap-6 lg:gap-8">
          {cards.length ? (
            cards
          ) : (
            <div className="w-full py-8 sm:py-12 text-center">
              <p className="mb-4 text-sm sm:text-base text-gray-600">
                No matching posts.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Pagination - works for both search results and server posts */}
      {totalPages > 1 &&
        (showPending ? (
          <BlogPaginationSkeleton />
        ) : (
          <div className="mt-4 sm:mt-8 md:mt-12 flex flex-wrap items-center justify-center gap-1.5 sm:gap-2">
            {/* Prev */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => handlePageNav(currentPage - 1)}
              disabled={!hasPrev}
              aria-label="Previous page"
              className="h-8 w-8 sm:h-10 sm:w-10"
            >
              <ChevronLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Button>

            {/* Page numbers with ellipsis */}
            <div className="flex flex-wrap gap-1 sm:gap-1.5">
              {visiblePages.map((item, idx) =>
                item === 'ellipsis' ? (
                  <span
                    key={`ellipsis-${idx}`}
                    className="flex h-8 w-6 sm:h-10 sm:w-8 items-center justify-center text-xs sm:text-sm text-gray-400"
                    aria-hidden
                  >
                    &hellip;
                  </span>
                ) : (
                  <Button
                    key={item}
                    variant={item === currentPage ? 'default' : 'outline'}
                    size="icon"
                    className="h-8 w-8 sm:h-10 sm:w-10 text-xs sm:text-sm"
                    onClick={() => handlePageNav(item)}
                    aria-current={item === currentPage ? 'page' : undefined}
                    aria-label={`Go to page ${item}`}
                  >
                    {item}
                  </Button>
                ),
              )}
            </div>

            {/* Next */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => handlePageNav(currentPage + 1)}
              disabled={!hasNext}
              aria-label="Next page"
              className="h-8 w-8 sm:h-10 sm:w-10"
            >
              <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Button>
          </div>
        ))}
    </div>
  )
}

export default ClientSearch
